import { TRACK_EVENT_COMMAND, PAGE_VIEW_EVENT, CLICK_EVENT } from "./constants";
import type { AnalyticsConfig, AnalyticsEngine, ReportingEventData, ReportingProperties } from "./types";

declare global {
  var reportingData: ReportingProperties;
  var usbAnalytics: AnalyticsEngine;
}

const DEFAULT_LANGUAGE = "en-us";

const getDefaultLanguage = (): string => {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem("language") || DEFAULT_LANGUAGE;
    }
  } catch {
    return DEFAULT_LANGUAGE;
  }
  return DEFAULT_LANGUAGE;
};

const buildPageName = (...segments: (string | undefined)[]): string => {
  return segments.filter(Boolean).join(":");
};

const pushAnalyticsEvent = (event: string, properties: ReportingProperties): void => {
  const sanitizedProperties = { ...properties };
  if (sanitizedProperties?.actionName) {
    delete sanitizedProperties.actionName;
  }
  globalThis.usbAnalytics?.push({
    command: TRACK_EVENT_COMMAND,
    event,
    properties: sanitizedProperties,
  });
};

export function createTracker(config: AnalyticsConfig) {
  const getLanguage = config.getLanguage ?? getDefaultLanguage;

  const trackPageView = (eventData: ReportingEventData): void => {
    const siteSection = eventData.siteSection || config.defaultSiteSection;
    const includeSubSiteSection = eventData.includeSubSiteSectionInPageName ?? true;
     const subSiteSectionSegment = includeSubSiteSection ? eventData.subSiteSection : undefined;
    const properties: ReportingProperties = {
      ...eventData,
      pageName: buildPageName(
        config.platformPrefix,
        siteSection,
        subSiteSectionSegment,
        eventData.actionName,
      ),
      platform: config.platform,
      siteSection: siteSection!,
      languageType: getLanguage(),
    };
    globalThis.reportingData = properties;
    pushAnalyticsEvent(PAGE_VIEW_EVENT, properties);
  };

  const trackClick = (eventData: ReportingEventData): void => {
    const siteSection = eventData.siteSection || config.defaultSiteSection;
    const includeSubSiteSection = eventData.includeSubSiteSectionInPageName ?? true;
    const subSiteSectionSegment = includeSubSiteSection ? eventData.subSiteSection : undefined;
    const properties: ReportingProperties = {
      ...eventData,
      linkName: buildPageName(
        config.platformPrefix,
        siteSection,
        subSiteSectionSegment,
        eventData.actionName ? `${eventData.actionName} click` : undefined,
      ),
      platform: config.platform,
      siteSection: siteSection!,
      languageType: getLanguage(),
    };
    pushAnalyticsEvent(CLICK_EVENT, properties);
  };

  return { trackPageView, trackClick };
}